import type React from 'react';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  applyPreferences,
  categories,
  COOKIE_CONSENT_KEY,
  COOKIE_PREFERENCES_KEY,
  ink,
  paper,
  Switch,
  type CookiePreferences,
} from '@/components/cookie-consent-parts';

// Cookie consent in the house style: a small ink-on-paper card in the bottom corner,
// hairlines, no icons, custom switches. Colors come from the theme tokens (--ink/--paper)
// so it reads correctly in both themes without relying on utility remaps.

export const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    let consent: string | null = null;
    let saved: string | null = null;
    try {
      consent = localStorage.getItem(COOKIE_CONSENT_KEY);
      saved = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    } catch {
      /* storage unavailable */
    }
    if (!consent) {
      const t = setTimeout(() => setShowBanner(true), 2000);
      return () => clearTimeout(t);
    }
    if (saved) {
      try {
        const prefs = JSON.parse(saved) as CookiePreferences;
        setPreferences(prefs);
        applyPreferences(prefs);
      } catch {
        /* ignore corrupt prefs */
      }
    }
  }, []);

  const commit = (prefs: CookiePreferences, verdict: 'accepted' | 'rejected') => {
    setPreferences(prefs);
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, verdict);
      localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    } catch {
      /* storage unavailable */
    }
    applyPreferences(prefs);
    setShowBanner(false);
    setShowDetails(false);
  };

  const acceptAll = () => commit({ necessary: true, analytics: true, marketing: true }, 'accepted');
  const essentialOnly = () =>
    commit({ necessary: true, analytics: false, marketing: false }, 'rejected');
  const saveSelected = () => commit(preferences, 'accepted');

  const solid: React.CSSProperties = { backgroundColor: ink, color: paper };
  const outline: React.CSSProperties = {
    color: ink,
    boxShadow: `inset 0 0 0 1.5px color-mix(in srgb, ${ink} 30%, transparent)`,
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 24, opacity: 0 }}
          transition={{ type: 'spring', damping: 26, stiffness: 220 }}
          className="fixed bottom-0 left-0 right-0 sm:left-4 sm:bottom-4 sm:right-auto z-50 p-2 sm:p-0 pointer-events-none"
          role="dialog"
          aria-label="Cookie preferences"
        >
          <div
            className="pointer-events-auto w-full sm:w-[380px] rounded-lg overflow-hidden"
            style={{
              backgroundColor: paper,
              color: ink,
              boxShadow: `0 1px 0 color-mix(in srgb, ${ink} 10%, transparent), 0 24px 60px -20px color-mix(in srgb, ${ink} 45%, transparent)`,
              border: `1px solid color-mix(in srgb, ${ink} 12%, transparent)`,
            }}
          >
            {/* summit edge accent */}
            <div
              className="h-[3px] w-full"
              style={{ background: 'linear-gradient(90deg, #2563eb, transparent 70%)' }}
            />

            <div className="p-3 sm:p-5">
              <div className="flex items-center sm:items-start justify-between gap-4">
                <div>
                  <p
                    className="hidden sm:block text-[10px] font-medium uppercase tracking-[0.3em]"
                    style={{ opacity: 0.55 }}
                  >
                    Cookies
                  </p>
                  <h3 className="sm:mt-2 text-sm sm:text-base font-semibold tracking-tight">
                    {showDetails ? 'Choose what runs' : 'A few cookies, your call.'}
                  </h3>
                  <a
                    href="/privacy"
                    className="sm:hidden text-xs underline underline-offset-4"
                    style={{ opacity: 0.7 }}
                  >
                    Privacy policy
                  </a>
                </div>
                <button
                  type="button"
                  onClick={essentialOnly}
                  aria-label="Dismiss and keep essential cookies only"
                  className="-mr-1 -mt-1 h-8 w-8 rounded-md text-lg leading-none transition-opacity hover:opacity-100"
                  style={{ opacity: 0.5 }}
                >
                  ×
                </button>
              </div>

              {!showDetails ? (
                <p
                  className="hidden sm:block mt-2 text-sm leading-relaxed"
                  style={{ opacity: 0.72 }}
                >
                  Essential cookies keep the site running. Analytics and marketing stay off until
                  you say otherwise.{' '}
                  <a href="/privacy" className="underline underline-offset-4 hover:opacity-100">
                    Privacy policy
                  </a>
                </p>
              ) : (
                <ul
                  className="mt-4 divide-y"
                  style={{ borderColor: `color-mix(in srgb, ${ink} 10%, transparent)` }}
                >
                  {categories.map((c) => (
                    <li
                      key={c.key}
                      className="flex items-center justify-between gap-4 py-3"
                      style={{ borderColor: `color-mix(in srgb, ${ink} 10%, transparent)` }}
                    >
                      <div>
                        <p className="text-sm font-medium">{c.title}</p>
                        <p className="text-xs leading-relaxed" style={{ opacity: 0.62 }}>
                          {c.body}
                        </p>
                      </div>
                      <Switch
                        label={`${c.title} cookies`}
                        checked={preferences[c.key]}
                        disabled={c.locked}
                        onChange={(v) => setPreferences({ ...preferences, [c.key]: v })}
                      />
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-2 sm:mt-4 flex items-center gap-2">
                {!showDetails ? (
                  <>
                    <button
                      type="button"
                      onClick={acceptAll}
                      className="h-9 sm:h-10 flex-1 rounded-md text-sm font-semibold transition-opacity hover:opacity-90"
                      style={solid}
                    >
                      Accept all
                    </button>
                    <button
                      type="button"
                      onClick={essentialOnly}
                      className="h-9 sm:h-10 flex-1 rounded-md text-sm font-semibold transition-colors"
                      style={outline}
                    >
                      Essential only
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDetails(true)}
                      className="h-9 sm:h-10 px-3 rounded-md text-sm font-medium underline underline-offset-4"
                      style={{ opacity: 0.7 }}
                    >
                      Manage
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={saveSelected}
                      className="h-9 sm:h-10 flex-1 rounded-md text-sm font-semibold transition-opacity hover:opacity-90"
                      style={solid}
                    >
                      Save choices
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDetails(false)}
                      className="h-10 px-4 rounded-md text-sm font-semibold"
                      style={outline}
                    >
                      Back
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
